import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { startAuthentication } from '@simplewebauthn/browser'
import { useAuth } from '../hooks/useAuth'
import { apiClient } from '../services/api'
import { LoadingSpinner } from '../components/ui/LoadingSpinner'

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
})

type LoginForm = z.infer<typeof loginSchema>

export function LoginPage() {
  const { login, webauthnLogin } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [webauthnBusy, setWebauthnBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [mfaRequired, setMfaRequired] = useState(false)
  const [mfaPendingToken, setMfaPendingToken] = useState<string | null>(null)
  const [mfaMethods, setMfaMethods] = useState<string[]>([])
  const [totpCode, setTotpCode] = useState('')

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  })

  const onSubmit = async (data: LoginForm) => {
    setIsLoading(true)
    setError(null)

    try {
      await login(data.email, data.password, mfaRequired ? totpCode : undefined)
    } catch (err: any) {
      const code = err.error?.code
      if (code === 'MFA_REQUIRED') {
        setMfaRequired(true)
        setMfaPendingToken(err.error?.details?.mfaPendingToken ?? null)
        setMfaMethods(err.error?.details?.methods ?? [])
        setError(null)
      } else if (code === 'INVALID_MFA') {
        setMfaRequired(true)
        setError('That code was not valid. Try again.')
      } else {
        setError(err.error?.message || 'Login failed')
      }
    } finally {
      setIsLoading(false)
    }
  }

  /** Run a WebAuthn/FIDO2 login ceremony (security key / passkey). */
  const onUseSecurityKey = async () => {
    if (!mfaPendingToken) return
    setWebauthnBusy(true)
    setError(null)
    try {
      const { data } = await apiClient.webauthnLoginOptions(mfaPendingToken)
      const assertion = await startAuthentication({ optionsJSON: data.options })
      await webauthnLogin(mfaPendingToken, assertion)
      // AuthProvider sets the user; the router redirects automatically.
    } catch (err: any) {
      if (err?.name === 'NotAllowedError' || err?.code === 'USER_CANCELLED') {
        setError('Security key prompt cancelled.')
      } else if (err?.error?.code === 'INVALID_MFA_SESSION') {
        setMfaRequired(false)
        setMfaPendingToken(null)
        setError('Your sign-in step expired. Enter your password again.')
      } else {
        setError(err?.error?.message || 'Security key verification failed.')
      }
    } finally {
      setWebauthnBusy(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-blue-100">
            <svg
              className="h-6 w-6 text-blue-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
              />
            </svg>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Sign in to CMMC Platform
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Access your CMMC compliance dashboard
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <input
                id="email"
                {...register('email')}
                type="email"
                autoComplete="email"
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Enter your email"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

            <div>
              <div className="flex justify-between">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <a href="/forgot-password" className="text-sm font-medium text-blue-600 hover:text-blue-500">
                  Forgot your password?
                </a>
              </div>
              <input
                id="password"
                {...register('password')}
                type="password"
                autoComplete="current-password"
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Enter your password"
              />
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
              )}
            </div>

            {mfaRequired && (
              <div>
                <label htmlFor="totpCode" className="block text-sm font-medium text-gray-700">
                  Authentication code
                </label>
                <input
                  id="totpCode"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value)}
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm tracking-widest"
                  placeholder="6-digit code or backup code"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Enter the code from your authenticator app, or a backup code.
                </p>
              </div>
            )}
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <LoadingSpinner size="sm" className="text-white" />
              ) : (
                'Sign in'
              )}
            </button>
          </div>

          {mfaRequired && mfaMethods.includes('webauthn') && (
            <div>
              <button
                type="button"
                onClick={onUseSecurityKey}
                disabled={webauthnBusy || isLoading}
                className="w-full flex justify-center items-center py-2 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {webauthnBusy ? (
                  <LoadingSpinner size="sm" className="text-gray-500" />
                ) : (
                  <>
                    <svg className="h-4 w-4 mr-2 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                    </svg>
                    Use security key (YubiKey)
                  </>
                )}
              </button>
            </div>
          )}

          <div className="text-center">
            <p className="text-sm text-gray-600">
              Don't have an account?{' '}
              <a href="/register" className="font-medium text-blue-600 hover:text-blue-500">
                Sign up
              </a>
            </p>
          </div>
        </form>
      </div>
    </div>
  )
}
